import { getBusinessCategory } from './businessCategories';
import { getPlatformSelections } from './clientPayload';

const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

const TIME_ZONE_OPTIONS = {
  'Eastern Daylight Time - (GMT-4)': 'Eastern',
  'Central Daylight Time - (GMT-5)': 'Central',
  'Mountain Daylight Time - (GMT-6)': 'Mountain',
  'Mountain Standard Time - (GMT-7)': 'Mountain – Arizona (no daylight saving)',
  'Pacific Daylight Time - (GMT-7)': 'Pacific',
  'Alaska Daylight Time - (GMT-8)': 'Alaska',
  'Hawaii-Aleutian Standard Time - (GMT-10)': 'Hawaii'
};

const getOnboardingTimezone = (timeZone) => {
  if (TIME_ZONE_OPTIONS[timeZone]) return TIME_ZONE_OPTIONS[timeZone];
  if (/^eastern\b/i.test(timeZone || '')) return 'Eastern';
  if (/^central\b/i.test(timeZone || '')) return 'Central';
  if (/^mountain standard/i.test(timeZone || '')) {
    return 'Mountain – Arizona (no daylight saving)';
  }
  if (/^mountain\b/i.test(timeZone || '')) return 'Mountain';
  if (/^pacific\b/i.test(timeZone || '')) return 'Pacific';
  if (/^alaska\b/i.test(timeZone || '')) return 'Alaska';
  if (/^hawaii/i.test(timeZone || '')) return 'Hawaii';
  return timeZone;
};

const toText = (value) =>
  Array.isArray(value) ? value.join('\n') : String(value || '');

const toWebsiteColorText = (value) => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(', ');
  if (value && typeof value === 'object') {
    return Object.values(value)
      .filter((color) => typeof color === 'string')
      .join(', ');
  }
  return '';
};

const getStoredHours = (businessHours) => {
  if (!businessHours || typeof businessHours !== 'object') return undefined;

  const days = {};
  let hasHours = false;

  DAY_KEYS.forEach((day) => {
    days[day] = {
      isOpen: true,
      open24: false,
      openTime: '09:00',
      closeTime: '17:00'
    };
    const storedHours = businessHours[day];
    if (typeof storedHours !== 'string' || !storedHours) return;

    if (storedHours === '/') {
      hasHours = true;
      days[day] = {
        isOpen: false,
        open24: false,
        openTime: '09:00',
        closeTime: '17:00'
      };
      return;
    }

    if (storedHours === '0/24') {
      hasHours = true;
      days[day] = {
        isOpen: true,
        open24: true,
        openTime: '00:00',
        closeTime: '00:00'
      };
      return;
    }

    const match = storedHours.match(/^(\d{1,2})\/(\d{1,2})$/);
    if (!match) return;
    hasHours = true;
    days[day] = {
      isOpen: true,
      open24: false,
      openTime: `${match[1].padStart(2, '0')}:00`,
      closeTime: `${match[2].padStart(2, '0')}:00`
    };
  });

  return hasHours ? { days } : undefined;
};

const getStoredFields = (value, fieldNames) => {
  if (!value || typeof value !== 'object') return undefined;
  const vals = fieldNames.map((fieldName) => value[fieldName] || '');
  return vals.some(Boolean) || value.unsure
    ? { vals, unsure: Boolean(value.unsure) }
    : undefined;
};

export const mapClientToOnboardingAnswers = (client = {}) => {
  const mergeFields = client.mergeFields || {};
  const website = mergeFields['~Website'] || '';
  const secondaryPhone =
    mergeFields['~SecondaryPhone'] || client.secondaryPhone || '';
  const answers = {
    platforms: getPlatformSelections(client.clientFlags || []),
    website: { url: website, none: !website }
  };
  const setIfPresent = (key, value) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      (!Array.isArray(value) || value.length > 0)
    ) {
      answers[key] = value;
    }
  };

  setIfPresent('name', client.clientName || mergeFields['~Name']);
  setIfPresent('phone', mergeFields['~Phone']);
  setIfPresent('phone2', secondaryPhone);
  if (secondaryPhone) {
    answers.phoneShown =
      mergeFields['~Phone'] === secondaryPhone
        ? 'Second number'
        : 'Main number';
  }
  setIfPresent('email', mergeFields['~Email']);
  if (mergeFields['~Address']) {
    answers.address = { street: mergeFields['~Address'], suite: '' };
  }
  if (website) answers.website = { url: website, none: false };

  const timezone = getOnboardingTimezone(client.timeZone);
  setIfPresent('timezone', timezone);
  setIfPresent('areas', toText(client.serviceArea));
  setIfPresent('special', client.description);
  setIfPresent(
    'businessType',
    client.industry && getBusinessCategory(client.industry)
  );
  const hours = getStoredHours(client.businessHours);
  if (hours) {
    if (client.emergencyService) hours.emergency = client.emergencyService;
    answers.hours = hours;
  }
  setIfPresent('services', toText(client.primaryService));
  setIfPresent('priorityServices', toText(client.priorityServices));
  setIfPresent('specials', toText(client.specials));
  setIfPresent('gbpOffers', client.gbpOffers);
  setIfPresent('estimates', client.estimates);
  setIfPresent('avoid', toText(client.negativeKeywords));
  setIfPresent('holidays', client.holidays);
  setIfPresent(
    'licensed',
    typeof client.licensed === 'boolean'
      ? client.licensed
        ? 'Licensed and insured'
        : 'Neither'
      : client.licensed
  );
  setIfPresent(
    'yearStarted',
    client.yearStarted === undefined ? undefined : String(client.yearStarted)
  );
  if (Array.isArray(client.clientFlags)) {
    answers.images = client.clientFlags.includes('NO_AI')
      ? 'Real photos only'
      : 'Real photos when I have them, AI images to fill the gaps';
  }
  const hashtags = [
    ...new Set([...(client.igTags || []), ...(client.fbTags || [])])
  ].join(' ');
  setIfPresent('hashtags', hashtags);
  setIfPresent(
    'teamSize',
    client.teamSize || (client.singlePerson ? 'Just me' : '')
  );
  setIfPresent('logoUrl', client.logoUrl);

  const gbpManager = getStoredFields(client.gbpManager, ['name', 'email']);
  if (gbpManager) answers.gbpManager = gbpManager;
  const domain = client.domainCompany;
  if (domain && typeof domain === 'object') {
    answers.domain = domain.unsure
      ? { unsure: true, vals: [] }
      : {
          vals: [
            domain.provider || '',
            domain.username || '',
            domain.password || ''
          ],
          unsure: false
        };
  }
  if (client.socialLinks && typeof client.socialLinks === 'object') {
    answers.socialLinks = client.socialLinks;
  }
  setIfPresent('goals', client.goals);
  setIfPresent('competitors', client.competitors);

  return {
    answers,
    websiteAnswers: {
      content:
        typeof client.websiteContent === 'string' ? client.websiteContent : '',
      colors: toWebsiteColorText(client.websiteColors),
      layout:
        typeof client.websiteLayout === 'string' ? client.websiteLayout : ''
    }
  };
};

const hasStoredAnswer = (key, value) => {
  if (value == null) return false;
  if (key === 'website') return Boolean(value.url?.trim());
  if (key === 'address')
    return Boolean(value.street?.trim() || value.suite?.trim());
  if (key === 'businessType') return Boolean(value.choice || value.other);
  if (key === 'hours') return Boolean(value.days);
  if (typeof value === 'string') return Boolean(value.trim());
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    return Object.entries(value).some(
      ([nestedKey, nestedValue]) =>
        nestedKey !== 'none' && hasStoredAnswer(nestedKey, nestedValue)
    );
  }
  return Boolean(value);
};

export const mergeGoogleAnswersIntoEmptyFields = (
  currentAnswers,
  googleAnswers
) =>
  Object.entries(googleAnswers).reduce(
    (answers, [key, value]) =>
      hasStoredAnswer(key, answers[key])
        ? answers
        : { ...answers, [key]: value },
    currentAnswers
  );
